/**
 * PostHog Cloud integration — the behavioral-analytics + session-replay track
 * (see ADR 0002 and docs/explanation/conversion-funnel-analytics.md). Distinct
 * from the first-party DIY funnel: PostHog owns autocapture + replay; our owned
 * milestone funnel stays in Postgres. Both key off the SAME `tenders_vid`, so a
 * person is one identity across DIY funnel + Meta + PostHog.
 *
 * DORMANT unless `VITE_POSTHOG_KEY` is set at build time — no SDK is loaded and
 * nothing is captured. The SDK is dynamic-imported (lazy, non-blocking) so it
 * never weighs down the initial mobile bundle.
 */
import type { PostHog } from 'posthog-js';
import { parseWebEnv } from './env';
import { getOrCreateVisitorId, rotateVisitorId } from './meta-cookies';
import { isInternalViewer } from './internal-viewer';
import { consentRequired, hasConsent } from './cookie-consent';
import { countryConfig } from '../country';

/** Matches our project's region (US Cloud, project 519110). Override via
 *  VITE_POSTHOG_HOST if the project ever moves region. */
const DEFAULT_HOST = 'https://us.i.posthog.com';

let phPromise: Promise<PostHog | null> | null = null;
/**
 * The RESOLVED client, kept alongside the promise.
 *
 * Everything else here is fire-and-forget and can await `phPromise`, but a
 * feature flag is read during render and must answer synchronously — a promise
 * cannot. Null until the lazy import lands, which is precisely why
 * `phFeatureFlag` treats "not yet" and "no PostHog" identically: both mean the
 * variant has not been assigned, so both must render control.
 */
let phClient: PostHog | null = null;

// ── Cookieless mode (`analytics.cookielessBeforeConsent`, Spain) ─────────────────
//
// Until the visitor grants «analíticas» — and after «Rechazar» — PostHog runs in a
// mode that stores NOTHING on the device and sees only a few funnel events. LSSI-CE
// art. 22.2 is about storing or reading information on the visitor's terminal; this
// mode does neither (in-memory identity, no cookie, no localStorage, no recording).
// «Aceptar» then switches the SAME instance to the full mode below, exactly as UZ/KZ
// always run it. Unset (UZ, KZ) or `features.cookieConsent` off: none of this runs.
//
// What it costs, said once: the visitor id lives in memory, so every page LOAD is a
// new anonymous visitor — a person who reloads, or comes back tomorrow, counts again,
// and nothing joins their visits. There is no replay, no autocapture, no person
// profile, and no `tenders_vid` — hence no join to Meta or to the first-party funnel.

// Optional chaining on purpose: unit tests mock `../country` with partial configs.
const COOKIELESS_FLAG =
  (countryConfig as Partial<typeof countryConfig> | undefined)?.analytics
    ?.cookielessBeforeConsent === true;

/** Does this deployment measure cookielessly before the banner is answered? */
export function cookielessBeforeConsent(): boolean {
  return COOKIELESS_FLAG && consentRequired();
}

/** Which way the SDK runs: `full` (cookies, replay — after a grant, and always on UZ/KZ). */
type PhMode = 'full' | 'cookieless';
let phMode: PhMode | null = null;

/**
 * The funnel events the cookieless mode lets through (besides the SDK's own
 * `$pageview`): a lot opened, a wall reached, a padlock tapped, the plans page, the
 * code asked for on /register. Nothing that carries free text (`search_performed`),
 * an account or a payment detail.
 */
export const COOKIELESS_EVENTS: ReadonlySet<string> = new Set([
  'tender_viewed',
  'paywall_shown',
  'upgrade_cta_clicked',
  'plans_viewed',
  'catalog_browsed',
  'reg_otp_requested',
]);

/** Query keys that can hold what a person typed — never sent, even without an identity. */
const FREE_TEXT_PARAMS = ['q', 'exclude', 'alert', 'email', 'token', 'code'];

/** The URL with the free-text keys removed (a malformed URL is returned untouched). */
export function cookielessUrl(url: unknown): unknown {
  if (typeof url !== 'string' || !url.includes('?')) return url;
  try {
    const u = new URL(url, 'https://x.invalid');
    for (const k of FREE_TEXT_PARAMS) u.searchParams.delete(k);
    const rel = `${u.pathname}${u.search}${u.hash}`;
    return /^https?:\/\//i.test(url) ? `${u.origin}${rel}` : rel;
  } catch {
    return url;
  }
}

/** A staff browser in cookieless mode: drop events without storing an opt-out flag. */
let cookielessMuted = false;

/**
 * Runs on every event in cookieless mode (`before_send`), after the SDK has built it:
 * lets only `$pageview` and {@link COOKIELESS_EVENTS} through, keeps the address and the
 * referrer free of typed text, and asks PostHog not to use the request's IP.
 *
 * ⚠️ The SDK's own `ip: false` is a NO-OP in posthog-js 1.407.2 (it logs «The `ip` config
 * option has NO EFFECT AT ALL»), so the IP is handled here: `$ip` is overridden with the
 * unspecified address and `$geoip_disable` is set. Whether PostHog's ingestion honours
 * both is a server-side fact this code cannot prove — check one cookieless event in
 * PostHog after the deploy (`$ip`, `$geoip_city_name` must be empty/0.0.0.0), and the
 * project setting «Discard client IP data» is the stronger switch if it is not.
 */
export function cookielessBeforeSend<
  E extends { event: string; properties?: Record<string, unknown> },
>(e: E | null): E | null {
  if (!e || phMode !== 'cookieless') return e;
  if (cookielessMuted) return null;
  if (e.event !== '$pageview' && !COOKIELESS_EVENTS.has(e.event)) return null;
  const props = { ...(e.properties ?? {}) };
  for (const k of ['$current_url', '$referrer']) {
    if (k in props) props[k] = cookielessUrl(props[k]);
  }
  // A company id on a profile wall: a sole trader's NIF is a person's number.
  delete props.tin;
  props.$ip = '0.0.0.0';
  props.$geoip_disable = true;
  return { ...e, properties: props } as E;
}

function apiKey(): string | undefined {
  try {
    return parseWebEnv(import.meta.env).VITE_POSTHOG_KEY;
  } catch {
    return undefined;
  }
}

/** The campaign tags PostHog reads off a landing URL (its own `utm_*` set). */
const LANDING_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/** The `utm_*` of a landing query, empty ones left out. */
export function landingCampaign(search: string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!search) return out;
  try {
    const params = new URLSearchParams(search);
    for (const k of LANDING_UTM_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 200);
    }
  } catch {
    /* malformed query — no campaign */
  }
  return out;
}

/*
 * The two option blocks the full mode shares between `init` and the switch from the
 * cookieless mode (`switchToFull`) — one definition, so the two cannot drift apart.
 */
/*
 * JS error tracking. `$exception` was 0 for 90 days — not because the app
 * throws nothing, but because exception capture was off
 * (`$exception_capture_enabled_server_side: false`), leaving us blind to
 * client crashes exactly as we scale ad traffic. Enabled here client-side,
 * which the SDK honours over the remote flag. The object form (not a bare
 * `true`) turns on unhandled errors + promise rejections but NOT console
 * errors — those are noisy and mostly third-party. The PostHog project's
 * Error Tracking product still has to be enabled to VIEW them; capture
 * flows regardless.
 */
const CAPTURE_EXCEPTIONS = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
};

/*
 * Session replay with masking: never record input values, and mask the
 * text of anything tagged `.ph-mask` / `[data-ph-mask]` (the revealed
 * заказчик contact). `.ph-no-capture` blocks an element entirely.
 *
 * `sampleRate` is set explicitly because this object REPLACES what
 * `defaults: '2026-05-30'` derives rather than merging into it. The
 * live remote config showed `sampleRate: null` — every session recorded
 * end to end, including every bot that clears PostHog's own UA filter.
 *
 * A client-side `sampleRate` takes precedence over the project setting
 * ("When set, takes precedence over the remote config" — the SDK
 * resolves it as `client ?? remote`), so this is the single source of
 * truth. It is deliberately low on UZ/KZ: the sessions actually worth
 * watching are opted back in at full rate by `phStartRecording()` (see
 * below), which beats paying to record every bounce on the off-chance one
 * of them converts. A deployment may raise it through
 * `analytics.replaySampleRate` (Spain: 1 — a few dozen visits a day, the
 * owner watches each one, 23.10).
 *
 * ⚠️ `strictMinimumDuration` ENFORCES NOTHING ON ITS OWN, and is kept
 * only so a floor works correctly if one is ever configured. It changes
 * HOW the minimum is compared (against buffered data rather than wall
 * clock), but the minimum ITSELF is remote-only:
 * `minimumDurationMilliseconds` does not exist in the client config
 * type at all, and the recorder reads it from
 * `$session_recording_remote_config`, which is currently `null`. A real
 * duration floor has to be set in PostHog project settings — it cannot
 * be shipped from here.
 */
const SESSION_RECORDING = {
  maskAllInputs: true,
  maskTextSelector: '.ph-mask, [data-ph-mask]',
  sampleRate:
    (countryConfig as Partial<typeof countryConfig> | undefined)?.analytics?.replaySampleRate ??
    0.1,
  strictMinimumDuration: true,
};

/** Where the SDK is told to send events, and where its console lives. */
function posthogHost(): string {
  try {
    return parseWebEnv(import.meta.env).VITE_POSTHOG_HOST ?? DEFAULT_HOST;
  } catch {
    return DEFAULT_HOST;
  }
}

/**
 * Campaign and visitor id on the SDK — the full mode's post-init steps (the visitor id
 * as a SUPER PROPERTY, the landing campaign and first touch). Shared by `init` and the
 * switch from the cookieless mode, which runs it on the grant: the campaign then comes
 * from the address the visit ARRIVED on, kept in memory by the consent bootstrap.
 */
function registerVisitor(
  posthog: PostHog,
  opts: { landingSearch?: string; landingPath?: string },
): void {
  /*
   * Carry the shared visitor id as a SUPER PROPERTY, not as
   * `bootstrap.distinctID`.
   *
   * Bootstrapping it hijacked PostHog's own identity. In the installed SDK
   * both bootstrap guards are gated on `isIdentifiedID`, which we did not
   * set, so every init took the unguarded branch and force-wrote
   * `distinct_id = vid` plus `$user_state = 'anonymous'` — even for an
   * already-identified user — firing a redundant `$identify` on every full
   * page load.
   *
   * Setting `isIdentifiedID: true` is NOT the fix: PostHog's docs say to
   * set it false "if you're generating a random anonymous ID", which is
   * exactly what `tenders_vid` is. True would mark every anonymous visitor
   * as identified and spawn a person profile per visitor, defeating
   * `person_profiles: 'identified_only'` above.
   *
   * As a super property the id rides on every event, so the DIY funnel and
   * Meta still join to PostHog — without touching PostHog's identity.
   */
  const vid = getOrCreateVisitorId();
  if (vid) posthog.register({ tenders_vid: vid });
  if (opts.landingSearch !== undefined || opts.landingPath !== undefined) {
    const campaign = landingCampaign(opts.landingSearch);
    try {
      if (Object.keys(campaign).length > 0) posthog.register_for_session(campaign);
      // The SDK's own shape (`getPersonInfo`: r = referrer or '$direct', u = URL);
      // `register_once` keeps a value an earlier session already stored.
      const landingHref = `${window.location.origin}${opts.landingPath ?? window.location.pathname}${opts.landingSearch ?? ''}`;
      posthog.persistence?.register_once(
        {
          $initial_person_info: {
            r: (document.referrer || '$direct').slice(0, 1000),
            u: landingHref.slice(0, 1000),
          },
        },
        undefined,
      );
    } catch {
      /* an SDK without these internals — the campaign is lost, as before */
    }
  }
}

/** A logged-in visitor met while cookieless: identified only if they later grant. */
let pendingIdentify: { userId: string; props?: Record<string, unknown> } | null = null;

/**
 * Start PostHog in the cookieless mode (see the note at the top). Idempotent. Nothing
 * here may write a cookie or a storage key: `persistence: 'memory'` keeps the SDK's
 * identity and session in the page; recording, autocapture, exceptions, surveys, flags
 * and person profiles are all off; `$pageview` and {@link COOKIELESS_EVENTS} are the
 * only events, filtered again in {@link cookielessBeforeSend}.
 */
function startCookieless(): void {
  const key = apiKey();
  if (!key || phPromise) return;
  // Staff and test browsers get no analytics at all, as in the full mode.
  if (isInternalViewer()) return;
  phMode = 'cookieless';
  const host = posthogHost();
  phPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      phClient = posthog;
      posthog.init(key, {
        api_host: host,
        ui_host: 'https://us.posthog.com',
        defaults: '2026-05-30',
        persistence: 'memory',
        disable_persistence: true,
        disable_session_recording: true,
        autocapture: false,
        capture_pageview: 'history_change',
        capture_pageleave: false,
        capture_exceptions: false,
        // No profile, no `$set` / `$set_once` on any event.
        person_profiles: 'never',
        advanced_disable_flags: true,
        disable_surveys: true,
        disable_conversations: true,
        disable_product_tours: true,
        // The device-model lookup reads high-entropy client hints.
        disableDeviceModel: true,
        // Click ids (`fbclid`, `gclid`, …) are replaced in the URLs the SDK sends.
        mask_personal_data_properties: true,
        before_send: cookielessBeforeSend,
      });
      return posthog;
    })
    .catch((err: unknown) => {
      // Never throws into the page — but says so: a measuring mode that failed silently
      // looks exactly like one that works and counts nothing.
      console.warn('[posthog] cookieless mode did not start', err);
      return null;
    });
}

/**
 * The visitor granted «analíticas» while the cookieless SDK was running: the SAME
 * instance takes on the full mode (cookies, replay, autocapture, exceptions, person
 * profiles) with `set_config`, which the installed SDK applies at run time — it
 * migrates the in-memory identity into the new storage, starts the recorder and
 * autocapture. Nothing is replayed from before the grant, and nothing is lost: the
 * page's anonymous id simply becomes the persistent one.
 */
async function switchToFull(opts: { landingSearch?: string; landingPath?: string }): Promise<void> {
  const p = await phPromise;
  if (!p) return;
  try {
    p.set_config({
      persistence: 'localStorage+cookie',
      disable_persistence: false,
      disable_session_recording: false,
      autocapture: true,
      capture_pageleave: 'if_capture_pageview',
      capture_exceptions: CAPTURE_EXCEPTIONS,
      session_recording: SESSION_RECORDING,
      person_profiles: 'identified_only',
      advanced_disable_flags: false,
      disableDeviceModel: false,
      mask_personal_data_properties: false,
    });
    registerVisitor(p, opts);
    if (pendingIdentify) {
      p.identify(pendingIdentify.userId, pendingIdentify.props);
      pendingIdentify = null;
    }
  } catch (err) {
    // The SDK refused the switch — it stays cookieless, which is the safe side. Said aloud:
    // a visitor who accepted and is still counted as one who has not would be invisible.
    console.warn('[posthog] could not switch to the full mode', err);
    phMode = 'cookieless';
  }
}

/**
 * Lazily initialise PostHog. Safe to call more than once (idempotent) and on
 * SSR (no-ops). No-op when no key is configured. Call once, post-hydration.
 *
 * `landingSearch` / `landingPath` — cookie consent only (`consent-trackers.ts`):
 * the address the visit arrived on. The SDK reads `utm_*` off the CURRENT
 * address when it starts, and on a consent deployment it starts when the
 * visitor grants — after they have often navigated away from the ad's landing
 * URL. Both are set BEFORE the first `$pageview` (the SDK captures it on the
 * next tick): the campaign as session properties, so that pageview and every
 * event after it carry it; and the landing URL as the person's initial URL
 * (`$initial_person_info`, which the SDK writes once and turns into
 * `$initial_utm_*` / `$initial_pathname`), so the person's first touch is the
 * ad, not the page they accepted on. UZ/KZ call this with no argument and
 * nothing changes.
 */
export function initPostHog(opts: { landingSearch?: string; landingPath?: string } = {}): void {
  if (typeof window === 'undefined') return;
  // Cookie consent (`features.cookieConsent`): no `ph_*` cookie and no capture until
  // the visitor grants «analíticas» — except the cookieless mode (Spain,
  // `analytics.cookielessBeforeConsent`), which stores nothing. The banner calls this
  // again on the grant. Always granted on UZ/KZ — see lib/cookie-consent.ts.
  if (!hasConsent('analytics')) {
    if (cookielessBeforeConsent()) startCookieless();
    return;
  }
  const key = apiKey();
  if (!key) return;
  if (phPromise) {
    // The visitor granted while the cookieless SDK was running: switch that one
    // instance to the full mode rather than start a second.
    if (phMode === 'cookieless') {
      // Flipped HERE, not inside the async switch: the grant handler calls `trackOnce('session_start')`
      // right after this returns, and the allow-list in `phCapture` must already read «full». The
      // switch itself still runs first — it was queued before that capture's `then`.
      phMode = 'full';
      void switchToFull(opts);
    }
    return;
  }
  phMode = 'full';

  const host = posthogHost();

  phPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      phClient = posthog;
      posthog.init(key, {
        api_host: host,
        // When api_host is our first-party reverse proxy (https://uztender.com/ingest,
        // set in CI to dodge ad blockers that blocklist *.posthog.com), the SDK can't
        // infer where the PostHog app itself lives — point the toolbar / "view in
        // PostHog" links at the real US console. Harmless when api_host is direct.
        ui_host: 'https://us.posthog.com',
        // Modern defaults (SPA pageviews on history change, sensible session
        // recording — strictMinimumDuration + canvas capture). Matches the
        // version PostHog suggested for this project.
        defaults: '2026-05-30',
        // Only create person profiles for identified (logged-in) users; anonymous
        // events still capture but don't spawn a profile — privacy + cost.
        person_profiles: 'identified_only',
        autocapture: true,
        capture_pageview: 'history_change',
        capture_exceptions: CAPTURE_EXCEPTIONS,
        session_recording: SESSION_RECORDING,
      });
      registerVisitor(posthog, opts);
      return posthog;
    })
    .catch(() => null);
}

/**
 * Staff / test sessions: stop capturing entirely (events, replays, autocapture).
 * Opt back in only if this browser had been opted out — the default state is
 * opted-in, and an unconditional `opt_in_capturing()` would emit an `$opt_in`
 * event on every ordinary login. See `setInternalViewer` in tracking.ts for the
 * Meta/GA half and the localStorage memory that also covers anonymous loads.
 */
export function phSetInternal(internal: boolean): void {
  if (!phPromise) return;
  // Cookieless mode: an opt-out would be written to localStorage, so the staff
  // browser is muted in memory instead (`cookielessBeforeSend` drops everything).
  if (phMode === 'cookieless') {
    cookielessMuted = internal;
    return;
  }
  void phPromise.then((p) => {
    if (!p) return;
    if (internal) p.opt_out_capturing();
    else if (p.has_opted_out_capturing()) p.opt_in_capturing();
  });
}

/**
 * Cookie consent: «analíticas» was just REVOKED. Stop recording and capturing
 * at once, so nothing queued is flushed on the way out; the caller then deletes
 * PostHog's cookies and storage and reloads the page (`consent-trackers.ts`),
 * after which `initPostHog` never runs again without a new grant. A no-op when
 * PostHog never started.
 */
export async function phStopForRevocation(): Promise<void> {
  if (!phPromise) return;
  const p = await phPromise;
  if (!p) return;
  try {
    p.stopSessionRecording();
  } catch {
    /* recorder not loaded — nothing to stop */
  }
  try {
    p.opt_out_capturing();
  } catch {
    /* already gone */
  }
}

/** Link the current (anonymous) session to a user on login/register. */
export function phIdentify(userId: string, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  // Cookieless mode never links an event to an account; the visitor is identified
  // only if they later grant (`switchToFull`).
  if (phMode === 'cookieless') {
    pendingIdentify = { userId, props };
    return;
  }
  void phPromise.then((p) => p?.identify(userId, props));
}

/**
 * Detach the identity on logout (starts a fresh anonymous session).
 *
 * Rotates `tenders_vid` too, and does so even when PostHog is dormant: the id
 * is shared with Meta's `external_id` and the DIY funnel's `anon_id`, so
 * leaving it in place would let the next person to log in on this browser be
 * merged with the one who just left. See `rotateVisitorId`.
 */
export function phReset(): void {
  rotateVisitorId();
  if (!phPromise) return;
  if (phMode === 'cookieless') {
    pendingIdentify = null;
    return;
  }
  void phPromise.then((p) => {
    p?.reset();
    // `reset()` clears super properties as well, so re-register the NEW id —
    // otherwise every post-logout event ships without the join key.
    const vid = getOrCreateVisitorId();
    if (vid) p?.register({ tenders_vid: vid });
  });
}

/**
 * Force this session to be recorded regardless of the 10% sample.
 *
 * Sampling is the right default for a catalog people bounce off, but it is the
 * wrong default for the handful of sessions we most need to watch: over a
 * fortnight there were 39 `checkout_started` against ~27k sessions, so a flat
 * 10% sample would capture roughly four of them — too few to see a pattern in
 * the step where most purchase intent is lost.
 *
 * Call this at the moment a session becomes high-value (checkout). Recording
 * starts from the call onward, not retroactively, which is the trade for not
 * paying to record everything.
 */
export function phStartRecording(): void {
  if (isInternalViewer()) return;
  if (!phPromise) return;
  // Recording needs the grant: the cookieless mode never records, not even at checkout.
  if (phMode === 'cookieless') return;
  /*
   * `true` is load-bearing. Called with NO argument this is a no-op against
   * sampling — verified in the shipped SDK (posthog-js@1.407.2):
   *
   *   startSessionRecording(t) {
   *     var o = (t === true),
   *         a = { sampling: o || !(t == null || !t.sampling), … };
   *     Object.values(a).some(Boolean) && ( … overrideSampling() … )
   *   }
   *
   * With no argument every flag is false, `.some(Boolean)` is false, and no
   * override runs — the call only clears `disable_session_recording`. So the
   * 10% sample still applied at checkout and ~90% of the sessions this exists
   * to capture were dropped, while the code read as though they were not.
   *
   * `true` means "ignore all ingestion controls", which is exactly the intent:
   * a checkout is the one session we always want.
   */
  void phPromise.then((p) => p?.startSessionRecording(true));
}

/** Mirror a milestone event to PostHog (no-op when dormant). */
export function phCapture(event: string, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  // Cookieless mode: the funnel events on the allowlist only (the filter in
  // `cookielessBeforeSend` is the second lock on the same door).
  if (phMode === 'cookieless' && !COOKIELESS_EVENTS.has(event)) return;
  void phPromise.then((p) => p?.capture(event, props));
}

/**
 * Does PostHog take this event from a visitor who has not granted «analíticas»? True
 * only in the cookieless mode (Spain) and only for {@link COOKIELESS_EVENTS}. `track()`
 * asks this before it gives up on an event for lack of consent.
 */
export function phTakesWithoutConsent(event: string): boolean {
  return phMode === 'cookieless' && COOKIELESS_EVENTS.has(event);
}

/**
 * Report an error that a boundary CAUGHT (no-op when dormant).
 *
 * `capture_exceptions` above only sees errors that reach `window.onerror` or an
 * unhandled rejection. A render error caught by the router's `errorElement`
 * reaches neither, so without an explicit call it is invisible to Error
 * Tracking — see `lib/page-error.ts`. Never throws: the caller is an error
 * boundary, and a throw from the `.then` would surface as an unhandled
 * rejection of its own.
 */
export function phCaptureException(error: unknown, props?: Record<string, unknown>): void {
  if (!phPromise) return;
  if (phMode === 'cookieless') return;
  void phPromise.then((p) => {
    try {
      p?.captureException(error, props);
    } catch {
      // reporting must never break the page
    }
  });
}

/**
 * Read a PostHog feature flag, with the CONTROL branch as the only default.
 *
 * ⚠️ Every failure mode here must land on control, and there are more of them
 * than on most sites: PostHog is loaded lazily and is dormant without a key, an
 * ad-blocker kills the script outright for a share of real users, and flags
 * arrive asynchronously after `init` — so at first paint no flag is known yet.
 * A helper that returned `undefined` in those cases would push the branching
 * decision into every caller, and the caller that got it wrong would ship a
 * variant to people the experiment never assigned.
 *
 * So: `false` unless PostHog is loaded AND has positively said the flag is on.
 * That is the same shape `phCapture` uses for the same reason — dormant is a
 * normal state here, not an error.
 *
 * `onChange` fires once flags land, because the first render happens before
 * they do. Returns an unsubscribe.
 */
export function phFeatureFlag(key: string): boolean {
  if (!phClient) return false;
  return phClient.isFeatureEnabled(key) === true;
}

/** Subscribe to flag arrival/refresh. No-op (and a no-op unsubscribe) when dormant. */
export function phOnFeatureFlags(cb: () => void): () => void {
  if (!phPromise) return () => {};
  let cancelled = false;
  let off: (() => void) | undefined;
  void phPromise.then((p) => {
    if (!p || cancelled) return;
    off = p.onFeatureFlags(() => cb());
  });
  return () => {
    cancelled = true;
    off?.();
  };
}
